/**
 * PRP-MCP-9 work item 7 (G6): the admin review of assistant snapshots, over HTTP
 * (`/api/v1/admin/review`, admin only on the server).
 *
 * Its own module, like `mcpApi.ts`: `api.ts` sits at its god-file ceiling. The
 * shared `api` instance attaches the Firebase token and unwraps `response.data`.
 */
import { api } from './api';

/** The import question's answer (PRP-MCP-8 `export_import_answers`), or none yet. */
export type ImportAnswer = 'yes' | 'no' | 'not_yet';
/** The list filter: an answer, or `none` for snapshots with no answer yet. */
export type ImportAnswerFilter = ImportAnswer | 'none';

/** One row of the review list (never the snapshot body). */
export interface SnapshotRow {
  id: string;
  created_at: string;
  user_id: string;
  user_email: string | null;
  estimate_id: string;
  reason: 'export' | 'trial_end';
  chat_app: string | null;
  loss_type: string | null;
  room_count: number;
  line_count: number;
  chat_line_count: number;
  import_answer: ImportAnswer | null;
}

/** One line of a sanitized snapshot. */
export interface SnapshotLine {
  line: number;
  code: string;
  description: string;
  action: string | null;
  quantity: number | null;
  calc: string | null;
  unit: string | null;
  heading: string | null;
  from_chat: boolean;
}

export interface SnapshotRoom {
  name: string;
  dimensions: Record<string, number>;
  lines: SnapshotLine[];
}

export interface SnapshotChange {
  change_id: string;
  created_at: string | null;
  kind: string;
  room: string | null;
  line: { line_number: number; line_suffix: string } | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /** A fixed description (kind, code, action); no free-text request summary is kept (DD-9.C8). */
  operation: string;
  undone: boolean;
}

/** The sanitized estimate (app/mcp/snapshots.py build_snapshot). */
export interface SnapshotBody {
  schema: number;
  estimate_id: string;
  created_in_chat: boolean;
  chat_app: string | null;
  loss_type: string | null;
  is_flood_claim: boolean;
  state: string | null;
  water_category: string | null;
  rooms: SnapshotRoom[];
  changes: SnapshotChange[];
  original: { rooms: SnapshotRoom[] } | null;
}

export interface SnapshotDetail extends SnapshotRow {
  snapshot: SnapshotBody;
}

export interface SnapshotListParams {
  answer?: ImportAnswerFilter;
  date_from?: string;
  date_to?: string;
  user?: string;
  limit?: number;
  offset?: number;
}

export const AdminReviewAPI = {
  /** Snapshots newest first, filtered. */
  list: (params: SnapshotListParams = {}): Promise<{ items: SnapshotRow[]; total: number }> =>
    api.get('/admin/review/snapshots', { params }),

  /** One snapshot with its sanitized body. */
  get: (id: string): Promise<SnapshotDetail> =>
    api.get(`/admin/review/snapshots/${encodeURIComponent(id)}`),

  /** Deletes every snapshot of one user (the consent promise). */
  deleteForUser: (userId: string): Promise<{ user_id: string; deleted: number }> =>
    api.delete(`/admin/review/users/${encodeURIComponent(userId)}/snapshots`),
};
